import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";

// Pages
import Landing from "./pages/Landing";
import Pricing from "./pages/Pricing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import Activity from "./pages/Activity";
import Conversations from "./pages/Conversations";
import Settings from "./pages/Settings";
import ChatbotDetail from "./pages/ChatbotDetail";
import ChatbotTraining from "./pages/ChatbotTraining";
import ChatbotSettings from "./pages/ChatbotSettings";
import Account from "./pages/Account";
import WorkspaceManage from "./pages/WorkspaceManage";
import LiveChat from "./pages/LiveChat";
import ErrorLog from "./pages/ErrorLog";
import Appointments from "./pages/Appointments";
import Analytics from "./pages/Analytics";
import Webhooks from "./pages/Webhooks";
import Leads from "./pages/Leads";
import PublicChat from "./pages/PublicChat";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import Contact from "./pages/Contact";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import Download from "./pages/Download";
import UserManagement from "./pages/UserManagement";

// Layout
import DashboardLayout from "./components/layout/DashboardLayout";

const HOME = "/home";

function Splash() {
  return (
    <div className="b2 min-h-screen flex items-center justify-center bg-b2-canvas">
      <div className="w-7 h-7 rounded-full border-2 border-b2-indigo border-t-transparent animate-spin" />
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <Splash />;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Splash />;
  if (user) return <Navigate to={HOME} replace />;
  return children;
}

/* Every signed-in page wears the same shell. */
function Shell({ children }) {
  return (
    <ProtectedRoute>
      <DashboardLayout>{children}</DashboardLayout>
    </ProtectedRoute>
  );
}

/* BC-021: older links (/chat-history?conv=...) land on Conversations with
   their query string, instead of falling through to the landing page. */
function ToConversations() {
  const { search } = useLocation();
  return <Navigate to={`/conversations${search}`} replace />;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/" element={<Landing />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
      <Route path="/chat/:chatbotId" element={<PublicChat />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/contact-us" element={<Contact />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/download" element={<Download />} />

      {/* The five everyday destinations */}
      <Route path={HOME} element={<Shell><Dashboard /></Shell>} />
      <Route path="/conversations" element={<Shell><Conversations /></Shell>} />
      <Route path="/contacts" element={<Shell><Leads /></Shell>} />
      <Route path="/appointments" element={<Shell><Appointments /></Shell>} />
      <Route path="/insights" element={<Shell><Analytics /></Shell>} />

      {/* Reached from Home */}
      <Route path="/activity" element={<Shell><Activity /></Shell>} />
      <Route path="/chatbot/:id" element={<Shell><ChatbotDetail /></Shell>} />
      <Route path="/chatbot/:id/training" element={<Shell><ChatbotTraining /></Shell>} />
      <Route path="/chatbot/:id/settings" element={<Shell><ChatbotSettings /></Shell>} />

      {/* Settings: General, Team, Plan & usage, Advanced. Each tab is a real
          address so links into a tab survive. */}
      <Route path="/settings" element={<Navigate to="/settings/general" replace />} />
      <Route path="/settings/:tab" element={<Shell><Settings /></Shell>} />
      <Route path="/account" element={<Shell><Account /></Shell>} />
      <Route path="/usage" element={<Navigate to="/settings/plan" replace />} />
      <Route path="/billing" element={<Navigate to="/settings/plan" replace />} />

      {/* Advanced — kept at their original paths so existing links survive */}
      <Route path="/workspace/manage" element={<Shell><WorkspaceManage /></Shell>} />
      {/* Live chat now lives inside the Conversations inbox. The old page is
          kept mounted at a legacy path in case a workflow still depends on
          it, but nothing links to it. */}
      <Route path="/workspace/livechat-legacy" element={<Shell><LiveChat /></Shell>} />
      <Route path="/workspace/errorlog" element={<Shell><ErrorLog /></Shell>} />
      <Route path="/workspace/webhooks" element={<Shell><Webhooks /></Shell>} />
      <Route path="/admin/users" element={<Shell><UserManagement /></Shell>} />

      {/* Old addresses keep working */}
      <Route path="/my-chatbots" element={<Navigate to={HOME} replace />} />
      <Route path="/workspace/chat-history" element={<ToConversations />} />
      <Route path="/chat-history" element={<ToConversations />} />
      <Route path="/workspace/livechat" element={<Navigate to="/conversations" replace />} />
      <Route path="/workspace/leads" element={<Navigate to="/contacts" replace />} />
      {/* Lead-notification emails sent before the rename link here */}
      <Route path="/leads" element={<Navigate to="/contacts" replace />} />
      <Route path="/workspace/appointments" element={<Navigate to="/appointments" replace />} />
      <Route path="/workspace/visitor-analytics" element={<Navigate to="/insights" replace />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" richColors closeButton />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
